import { useEffect, useState } from 'react'
import { data as remixData, useNavigate, useSearchParams } from 'react-router'
import { ManifestacionBienesActivosScreen } from '#app/components/cotizacion/screens/manifestacion-bienes/ManifestacionBienesActivosScreen'
import { ManifestacionBienesConfirmationScreen } from '#app/components/cotizacion/screens/manifestacion-bienes/ManifestacionBienesConfirmationScreen'
import { ManifestacionBienesIntroScreen } from '#app/components/cotizacion/screens/manifestacion-bienes/ManifestacionBienesIntroScreen'
import { ManifestacionBienesPasivosScreen } from '#app/components/cotizacion/screens/manifestacion-bienes/ManifestacionBienesPasivosScreen'
import { ManifestacionBienesPersonalScreen } from '#app/components/cotizacion/screens/manifestacion-bienes/ManifestacionBienesPersonalScreen'
import { Icon } from '#app/components/ui/icon'
import { Modal } from '#app/components/ui/modal'
import { ProgressBar } from '#app/components/ui/progress-bar'
import { sendManifestacionBienesEmail } from '#app/utils/email.server.ts'
import { type Route } from './+types/manifestacion-bienes'

export const meta: Route.MetaFunction = () => [
	{ title: 'Manifestación de Bienes - 100prevalor' },
	{
		name: 'description',
		content: 'Declaración jurada de patrimonio para pólizas de caución',
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const quoteDataString = formData.get('quoteData')

	if (typeof quoteDataString !== 'string') {
		return remixData(
			{ success: false, error: 'Datos inválidos' },
			{ status: 400 },
		)
	}

	const quoteData = JSON.parse(quoteDataString) as ManifestacionBienesFormData

	try {
		await sendManifestacionBienesEmail(quoteData)
		return remixData({ success: true })
	} catch (error) {
		console.error('Error sending email:', error)
		return remixData(
			{ success: false, error: 'Error al enviar el email' },
			{ status: 500 },
		)
	}
}

type ManifestacionBienesFormData = {
	// Personal
	nombreApellido: string
	documento: string
	estadoCivil: string
	actividad: string
	localidad: string
	email: string
	telefono: string
	// Activos
	inmuebles: string
	rodados: string
	valoresMobiliarios: string
	otrosActivos: string
	// Pasivos
	deudasHipotecarias: string
	deudasBancarias: string
	otrasDeudas: string
	// Cónyuge
	conyugeNombre: string
	conyugeDocumento: string
}

export default function ManifestacionBienes() {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const step = parseInt(searchParams.get('step') || '1', 10)

	const [completedSteps, setCompletedSteps] = useState<Set<number>>(
		new Set([1]),
	)
	const [formData, setFormData] = useState<
		Partial<ManifestacionBienesFormData>
	>({})

	const totalSteps = 5

	useEffect(() => {
		if (!completedSteps.has(step)) {
			const lastCompleted = Math.max(...Array.from(completedSteps))
			setSearchParams({ step: String(lastCompleted) })
		}
	}, [step, completedSteps, setSearchParams])

	const getProgressPercentage = () => {
		if (step <= 1) return 0
		const progressMap: Record<number, number> = {
			2: 25,
			3: 50,
			4: 75,
			5: 100,
		}
		return progressMap[step] || 0
	}

	const currentProgress = getProgressPercentage()
	const showProgressBar = step >= 2

	const handleNext = async (
		stepData?: Partial<ManifestacionBienesFormData>,
	) => {
		if (stepData) {
			setFormData({ ...formData, ...stepData })
		}

		if (step === 4) {
			const finalData = { ...formData, ...stepData }

			try {
				const formDataToSend = new FormData()
				formDataToSend.append('quoteData', JSON.stringify(finalData))

				const response = await fetch('/manifestacion-bienes', {
					method: 'POST',
					body: formDataToSend,
				})

				if (response.ok) {
					console.log('✅ Email Manifestación de Bienes enviado exitosamente')
				} else {
					console.error('❌ Error al enviar email Manifestación de Bienes')
				}
			} catch (error) {
				console.error(
					'❌ Error al enviar email Manifestación de Bienes:',
					error,
				)
			}
		}

		if (step < totalSteps) {
			setCompletedSteps((prev) => new Set([...prev, step + 1]))
			setSearchParams({ step: String(step + 1) })
		}
	}

	const handleBack = () => {
		if (step > 1) {
			setSearchParams({ step: String(step - 1) })
		}
	}

	const handleClose = () => {
		void navigate('/seguros')
	}

	const handleRestart = () => {
		setFormData({})
		setCompletedSteps(new Set([1]))
		setSearchParams({ step: '1' })
	}

	const renderStep = () => {
		switch (step) {
			case 1:
				return <ManifestacionBienesIntroScreen onNext={() => handleNext()} />
			case 2:
				return (
					<ManifestacionBienesPersonalScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={formData}
					/>
				)
			case 3:
				return (
					<ManifestacionBienesActivosScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={formData}
					/>
				)
			case 4:
				return (
					<ManifestacionBienesPasivosScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={formData}
						estadoCivil={formData.estadoCivil}
					/>
				)
			case 5:
				return (
					<ManifestacionBienesConfirmationScreen
						onBack={handleRestart}
						onClose={handleClose}
						data={formData as ManifestacionBienesFormData}
					/>
				)
			default:
				return <ManifestacionBienesIntroScreen onNext={() => handleNext()} />
		}
	}

	return (
		<>
			<div className="fixed inset-0 z-50 h-screen bg-neutral-50 md:hidden">
				<div className="flex h-full flex-col">
					<div className="flex shrink-0 items-center justify-end p-4">
						<button
							onClick={handleClose}
							className="flex items-center justify-center rounded-full p-2 transition-colors hover:bg-neutral-200"
							aria-label="Cerrar"
						>
							<Icon name="cross-1" className="size-6 text-neutral-900" />
						</button>
					</div>

					<div className="flex min-h-0 flex-1 flex-col gap-6 px-6 pb-8">
						{showProgressBar && (
							<ProgressBar progress={currentProgress} icon="file-text" />
						)}
						{renderStep()}
					</div>
				</div>
			</div>

			<div className="hidden md:block">
				<Modal
					isOpen={true}
					onClose={handleClose}
					showCloseButton={false}
					rounded={true}
					maxWidth={616}
				>
					<div className="relative flex h-[700px] w-[616px] flex-col rounded-3xl bg-white shadow-[0px_32px_48px_-12px_rgba(217,217,217,1)]">
						<div className="flex min-h-0 flex-1 flex-col gap-8 p-8">
							{showProgressBar && (
								<ProgressBar progress={currentProgress} icon="file-text" />
							)}

							<div className="flex min-h-0 flex-1 flex-col">{renderStep()}</div>
						</div>
					</div>
				</Modal>
			</div>
		</>
	)
}
